import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getStats, type Stats } from "@admin/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@admin/components/ui/card";

function StatCard({ label, value, to }: { label: string; value: number; to?: string }) {
  const card = (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-fg">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-semibold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
  if (!to) return card;
  return (
    <Link to={to} className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
      {card}
    </Link>
  );
}

export default function OverviewPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    getStats()
      .then(setStats)
      .catch(() => setErr("Could not load stats"));
  }, []);

  if (err) return <p className="text-danger">{err}</p>;
  if (!stats) return <p className="text-muted-fg">Loading...</p>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Overview</h1>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Pending contact" value={stats.pendingContactCount} />
        <StatCard label="Registered users" value={stats.userCount} />
        <StatCard label="Running VMs" value={stats.runningVmCount} />
        <StatCard label="Hibernated VMs" value={stats.hibernatedVmCount} to="/hibernation" />
        <StatCard label="Waking VMs" value={stats.wakingVmCount} to="/hibernation" />
        <StatCard label="Error VMs" value={stats.errorVmCount} />
        <StatCard label="Open incidents" value={stats.openIncidentCount} />
        <StatCard label="Open critical/warn" value={stats.openCriticalIncidentCount} />
        <StatCard label="Slack queue pending" value={stats.slackQueuePending} />
        <StatCard label="Slack queue processing" value={stats.slackQueueProcessing} />
      </div>
      {(stats.openIncidentCount > 0 || stats.openCriticalIncidentCount > 0) && (
        <p className="text-sm text-muted-fg">
          <Link to="/incidents" className="text-accent hover:underline">
            Review open incidents
          </Link>
        </p>
      )}
      {(stats.slackQueuePending > 0 || stats.slackQueueProcessing > 0) && (
        <p className="text-sm text-muted-fg">
          <Link to="/slack-queue" className="text-accent hover:underline">
            View Slack queue breakdown
          </Link>
        </p>
      )}
      {(stats.hibernatedVmCount > 0 || stats.wakingVmCount > 0) && (
        <p className="text-sm text-muted-fg">
          <Link to="/hibernation" className="text-accent hover:underline">
            View hibernation timeline
          </Link>
        </p>
      )}
    </div>
  );
}
